import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { chatApi } from '../api/chat';
import { useAuth } from '../hooks/useAuth';
import { showToast } from '../components/Toast';
import type { ChatConversation, ChatMessage, ChatStoreInquiry } from '../types';

/**
 * The conversations for one location: a list on the left, the live transcript
 * on the right. Reply = take over. Everything here reads from the transcript
 * store through the server (nothing is cached on this box), polling while the
 * tab is open — the store has no push channel by design.
 */

const INQUIRY_LABEL: Record<ChatStoreInquiry, string> = {
  home_care_lead: 'Home care',
  caregiver_lead: 'Caregiver (job)',
  ltci_policy_review: 'LTCi policy review',
  unknown: 'Not yet chosen',
};

const STATUS_LABEL: Record<ChatConversation['status'], string> = {
  open: 'Waiting',
  agent_engaged: 'In conversation',
  closed: 'Closed',
  abandoned: 'Abandoned',
};

function ago(iso: string | null): string {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function ChatInbox({ af }: { af: string }) {
  const [filter, setFilter] = useState<'active' | 'all'>('active');
  const [selected, setSelected] = useState<string | null>(null);

  const listQ = useQuery({
    queryKey: ['chat-conversations', af, filter],
    queryFn: () => chatApi.listConversations(af),
    refetchInterval: 5000,
  });

  if (listQ.isLoading) return <p style={muted}>Loading conversations…</p>;
  if (listQ.error) return <p style={muted}>{(listQ.error as Error).message}</p>;
  if (listQ.data && !listQ.data.storeEnabled) {
    return (
      <p style={muted}>
        The conversation store isn’t connected in this environment, so nothing is kept here yet.
      </p>
    );
  }

  const all = listQ.data?.conversations ?? [];
  const rows = filter === 'active' ? all.filter((c) => c.status === 'open' || c.status === 'agent_engaged') : all;
  const waiting = all.filter((c) => c.status === 'open').length;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600 }}>
          {waiting === 0 ? 'No one is waiting' : waiting === 1 ? '1 visitor waiting' : `${waiting} visitors waiting`}
        </span>
        <span style={{ flex: 1 }} />
        <label style={{ ...muted, display: 'flex', gap: 6, alignItems: 'center' }}>
          Show
          <select value={filter} onChange={(e) => setFilter(e.target.value as 'active' | 'all')} style={input}>
            <option value="active">Active</option>
            <option value="all">Everything</option>
          </select>
        </label>
      </div>

      {rows.length === 0 && (
        <p style={muted}>
          {filter === 'active' ? 'No active conversations right now.' : 'No conversations yet.'}
        </p>
      )}

      {/* Be explicit about the window. A list that silently stops at some depth
          reads as "these were not kept", which is a different and much worse
          claim than "you are looking at the last 90 days". */}
      {rows.length > 0 && listQ.data?.windowDays != null && (
        <p style={{ ...muted, marginTop: -4 }}>
          Showing the last {listQ.data.windowDays} days
          {listQ.data.truncated ? ' — only the most recent are listed; there are older ones in this window.' : '.'}
        </p>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: selected ? 'minmax(240px, 1fr) 2fr' : '1fr', gap: 14 }}>
        {rows.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 520, overflowY: 'auto' }}>
            {rows.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelected(c.id)}
                style={{
                  textAlign: 'left', border: '1px solid', borderColor: selected === c.id ? '#e87722' : '#e2e8f0',
                  background: selected === c.id ? '#fff7ed' : '#fff', borderRadius: 8, padding: '10px 12px', cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontWeight: 600 }}>{c.visitor_name || 'Website visitor'}</span>
                  <span style={muted}>{ago(c.last_message_at ?? c.started_at)}</span>
                </div>
                <div style={muted}>
                  {INQUIRY_LABEL[c.inquiry_type]} · {STATUS_LABEL[c.status]}
                  {c.assigned_agent?.display_name ? ` · ${c.assigned_agent.display_name}` : ''}
                </div>
                {c.last_message_preview && (
                  <div style={{ ...muted, marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {c.last_message_preview}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
        {selected && (
          <ConversationView key={selected} af={af} id={selected} onClose={() => setSelected(null)} />
        )}
      </div>
    </div>
  );
}

function ConversationView({ af, id, onClose }: { af: string; id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);

  const q = useQuery({
    queryKey: ['chat-conversation', af, id],
    queryFn: () => chatApi.getConversation(af, id),
    refetchInterval: (query) => (query.state.data?.conversation.status === 'closed' ? false : 3000),
  });

  const msgs: ChatMessage[] = q.data?.messages ?? [];
  useEffect(() => {
    const n = logRef.current;
    if (n) n.scrollTop = n.scrollHeight;
  }, [msgs.length]);

  async function act(fn: () => Promise<unknown>, okMsg?: string) {
    setBusy(true);
    try {
      await fn();
      if (okMsg) showToast(okMsg);
      await qc.invalidateQueries({ queryKey: ['chat-conversation', af, id] });
      await qc.invalidateQueries({ queryKey: ['chat-conversations', af] });
    } catch (e) {
      showToast((e as Error).message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  if (q.isLoading) return <p style={muted}>Opening…</p>;
  if (q.error || !q.data) return <p style={muted}>{(q.error as Error)?.message ?? 'Could not open this conversation.'}</p>;

  const c = q.data.conversation;
  const agents = q.data.agents;
  // A first-name-only greeting reads better than the full form value, and the
  // form takes first and last separately anyway.
  const firstName = (c.visitor_name || '').trim().split(/\s+/)[0] || 'there';
  // Signed off with whoever is looking at it — they are the franchise partner
  // sending it. mailto bodies are percent-encoded, and %0D%0A is the line break
  // every mail client agrees on.
  const signOff = me?.name?.trim() || 'The Amada Senior Care team';
  const replySubject = 'Thank you for reaching out to Amada Senior Care';
  const replyBody = [
    `Dear ${firstName},`,
    '',
    'Thank you for reaching out to Amada Senior Care. Our staff will be in touch '
      + "with you shortly. If you have additional questions, feel free to respond to "
      + "this email and we'll do our best to help.",
    '',
    signOff,
  ].join('\r\n');
  const mailto = c.visitor_email
    ? `mailto:${encodeURIComponent(c.visitor_email)}`
      + `?subject=${encodeURIComponent(replySubject)}`
      + `&body=${encodeURIComponent(replyBody)}`
    : null;

  const mine = c.assigned_user_id === me?.id;
  const closed = c.status === 'closed';
  const who = (uid: number | null) => (uid != null ? agents[String(uid)]?.display_name || `User #${uid}` : 'Team');

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, display: 'flex', flexDirection: 'column', minHeight: 420, maxHeight: 620 }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontWeight: 600 }}>{c.visitor_name || 'Website visitor'}</div>
          <div style={muted}>
            {INQUIRY_LABEL[c.inquiry_type]}
            {c.care_recipient ? ` · for ${c.care_recipient === 'self' ? 'themselves' : 'a loved one'}` : ''}
            {c.ltci_intent ? ` · ${c.ltci_intent === 'claim' ? 'file a claim' : 'review policy'}` : ''}
            {c.visitor_zip ? ` · ZIP ${c.visitor_zip}` : ''}
          </div>
          <div style={muted}>
            {c.visitor_phone && <a href={`tel:${c.visitor_phone}`}>{c.visitor_phone}</a>}
            {c.visitor_phone && c.visitor_email ? ' · ' : ''}
            {c.visitor_email && mailto && (
              <a href={mailto} title="Opens a reply already written, ready to send">
                {c.visitor_email}
              </a>
            )}
            {!c.visitor_phone && !c.visitor_email && 'No contact details yet'}
          </div>
        </div>
        <div style={muted}>
          {STATUS_LABEL[c.status]}{c.assigned_user_id != null ? ` · ${who(c.assigned_user_id)}` : ''}
        </div>
        {!closed && !mine && (
          <button style={primaryBtn} disabled={busy} onClick={() => act(() => chatApi.takeOver(af, id), 'You’re on the chat — the visitor can see you joined.')}>
            {c.assigned_user_id != null ? 'Take over' : 'Join chat'}
          </button>
        )}
        {!closed && (
          <button style={linkBtn} disabled={busy} onClick={() => act(() => chatApi.closeConversation(af, id), 'Conversation closed.')}>
            Close
          </button>
        )}
        <button style={linkBtn} onClick={onClose} aria-label="Hide conversation">Hide</button>
      </div>

      <div ref={logRef} style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 8, background: '#f8fafc' }}>
        {msgs.length === 0 && <p style={muted}>Nothing said yet.</p>}
        {msgs.map((m) => {
          const isAgent = m.role === 'agent';
          const isVisitor = m.role === 'visitor';
          return (
            <div key={m.seq} style={{ display: 'flex', justifyContent: isAgent ? 'flex-end' : 'flex-start' }}>
              <div style={{
                maxWidth: '78%', padding: '8px 12px', borderRadius: 12, fontSize: 14, lineHeight: 1.45,
                background: isAgent ? '#1f2a48' : isVisitor ? '#fff' : '#eef2f7',
                color: isAgent ? '#fff' : '#1c1c1c',
                border: isVisitor ? '1px solid #e2e8f0' : 'none',
              }}>
                <div style={{ fontSize: 11, opacity: 0.75, marginBottom: 2 }}>
                  {isAgent ? who(m.author_user_id) : isVisitor ? (c.visitor_name || 'Visitor') : 'Assistant'}
                  {' · '}{new Date(m.created_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                </div>
                {m.body}
              </div>
            </div>
          );
        })}
      </div>

      {!closed && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const text = draft.trim();
            if (!text) return;
            setDraft('');
            void act(() => chatApi.reply(af, id, text));
          }}
          style={{ display: 'flex', gap: 8, padding: 10, borderTop: '1px solid #e2e8f0' }}
        >
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={mine ? 'Reply to the visitor…' : 'Type to join and reply…'}
            style={{ ...input, flex: 1 }}
            disabled={busy}
          />
          <button type="submit" style={primaryBtn} disabled={busy || !draft.trim()}>Send</button>
        </form>
      )}
    </div>
  );
}

const muted: React.CSSProperties = { fontSize: 13, color: '#64748b' };
const input: React.CSSProperties = {
  padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 14, fontFamily: 'inherit',
};
const primaryBtn: React.CSSProperties = {
  padding: '8px 14px', borderRadius: 6, border: 'none', background: '#e87722', color: '#fff',
  fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};
const linkBtn: React.CSSProperties = {
  background: 'none', border: 'none', color: '#2563eb', textDecoration: 'underline',
  cursor: 'pointer', fontSize: 13, padding: 4, fontFamily: 'inherit',
};
